import React, { useCallback, useEffect, useRef, useState } from 'react';
import Topbar from '@/components/layout/Topbar';
import { api } from '@/lib/api';
import { Play, Check, Loader2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const STAGES = [
  { id: 'iniciando', label: 'Iniciando' },
  { id: 'snapshot', label: 'Mesa de datos · snapshot BTC/ETH mock' },
  { id: 'analistas', label: 'Analistas A1/A2/A3 (OpenAI)' },
  { id: 'estrategas', label: 'Estrategas S5-A / S5-B (Anthropic)' },
  { id: 'tribunal', label: 'Tribunal F1/F2/F3 (Gemini 2.5-pro)' },
  { id: 'comite', label: 'Comité · briefing' },
  { id: 'listo', label: 'Briefing generado' },
];

export default function Ejecutar() {
  const [runId, setRunId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const startPolling = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pipeline/status/${id}`);
        setProgress(data);
        if (data.done) {
          clearInterval(pollRef.current);
          setBusy(false);
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setError('Error consultando estado');
        setBusy(false);
      }
    }, 1200);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const run = async () => {
    setError(null); setBusy(true); setProgress({ stage: 'iniciando', detail: '', done: false });
    try {
      const { data } = await api.post('/pipeline/run');
      setRunId(data.run_id);
      startPolling(data.run_id);
    } catch (e) {
      setError('No se pudo iniciar el pipeline');
      setBusy(false);
    }
  };

  const currentIdx = STAGES.findIndex((s) => s.id === progress?.stage);
  const isError = progress?.stage === 'error';

  return (
    <>
      <Topbar title="Ejecutar pipeline" subtitle="Corre el pipeline cognitivo end-to-end con datos mock" />
      <div className="px-3 sm:px-4 lg:px-6 py-4 max-w-3xl">
        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base">Correr pipeline ahora</h2>
              <p className="text-xs text-muted-foreground mt-1">Snapshot mock → Analistas → Estrategas → Tribunal → Comité → Briefing. LLMs con diversidad de proveedores.</p>
            </div>
            <button
              data-testid="btn-run-pipeline"
              disabled={busy}
              onClick={run}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-70"
              style={{ background: 'hsl(var(--brand))' }}
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
              {busy ? 'Corriendo...' : 'Correr pipeline'}
            </button>
          </div>

          {error && <div className="mt-4 text-sm text-destructive">{error}</div>}

          {progress && (
            <ol className="mt-5 space-y-2" data-testid="pipeline-progress">
              {STAGES.map((s, i) => {
                let state = 'pending';
                if (isError && i > 0 && currentIdx >= i) state = 'error';
                else if (currentIdx > i) state = 'done';
                else if (currentIdx === i) state = progress.done ? 'done' : 'active';
                return (
                  <li key={s.id} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      state === 'done' ? 'bg-emerald-500/20 text-emerald-500' :
                      state === 'active' ? 'bg-[hsl(var(--brand))]/20 text-[hsl(var(--brand))]' :
                      state === 'error' ? 'bg-rose-500/20 text-rose-500' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {state === 'done' ? <Check size={14} /> : state === 'active' ? <Loader2 size={14} className="animate-spin" /> : state === 'error' ? <XCircle size={14} /> : i + 1}
                    </div>
                    <div className="text-sm">{s.label}</div>
                    {state === 'active' && progress.detail && (
                      <div className="text-xs text-muted-foreground ml-2">{progress.detail}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {progress?.done && !progress.error && (
            <div className="mt-5 panel-soft p-3 text-sm flex items-center justify-between">
              <span>Se generaron <strong>{(progress.candidatas_ids || []).length}</strong> candidatas.</span>
              <Link to="/dashboard" data-testid="link-goto-briefing" className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-white" style={{ background: 'hsl(var(--brand))' }}>Ver briefing</Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
